import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, ExternalLink, Eye, Flame, Layers, LayoutDashboard, Loader2, LogOut, Menu, Palette, QrCode, Rocket, UtensilsCrossed } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { LangSwitch } from "@/components/LangSwitch";
import { api } from "@/lib/api";
import { useAdminLang, useAuth } from "./context";
import { DashboardColor } from "./DashboardColor";
import { imgSrc } from "@/lib/media";
import { txt } from "@/lib/format";

const NAV = [
  { to: "/admin", key: "nav.overview", icon: LayoutDashboard, end: true, id: "overview" },
  { to: "/admin/menus", key: "nav.menus", icon: BookOpen, id: "menus" },
  { to: "/admin/items", key: "nav.items", icon: UtensilsCrossed, id: "items" },
  { to: "/admin/categories", key: "nav.categories", icon: Layers, id: "categories" },
  { to: "/admin/designer", key: "nav.designer", icon: Palette, id: "designer" },
  { to: "/admin/qr", key: "nav.qr", icon: QrCode, id: "qr" },
];

const NavLinks = ({ t, onNavigate }) => (
  <nav className="space-y-1">
    {NAV.map(({ to, key, icon: Icon, end, id }) => (
      <NavLink key={to} to={to} end={end} onClick={onNavigate} data-testid={`admin-sidebar-${id}`}
        className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
        <Icon className="h-[18px] w-[18px]" />
        {t(key)}
      </NavLink>
    ))}
  </nav>
);

const Brand = ({ t, brand, lang }) => (
  <div className="flex items-center gap-3 px-2">
    {brand?.logo_url ? (
      <img src={imgSrc(brand.logo_url, "sm")} alt="" data-testid="admin-brand-logo" className="h-9 w-auto max-w-[96px] shrink-0 object-contain" />
    ) : (
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary"><Flame className="h-5 w-5 text-white" /></span>
    )}
    <div className="min-w-0 leading-tight">
      <div className="truncate font-extrabold tracking-tight" data-testid="admin-brand-name">{(brand && txt(brand, "name", lang)) || "Wok Town"}</div>
      <div className="text-xs text-muted-foreground">{t("brand.dashboard")}</div>
    </div>
  </div>
);

const PublishControl = ({ stats, onPublish, busy, t }) => (
  <div className="flex items-center gap-2">
    <span data-testid="publish-status" className={`hidden items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold sm:inline-flex ${stats?.dirty ? "bg-amber-500/15 text-amber-300" : "bg-emerald-500/15 text-emerald-300"}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${stats?.dirty ? "bg-amber-400" : "bg-emerald-400"}`} />
      {stats?.dirty ? t("status.unpublished") : t("status.live")}
    </span>
    <Button size="sm" onClick={onPublish} disabled={busy || !stats?.dirty} data-testid="publish-btn" className="rounded-full">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
      {t("top.publish")}
    </Button>
  </div>
);

export default function AdminLayout() {
  const { t, lang, setLang, dir } = useAdminLang();
  const { logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const [brand, setBrand] = useState(null);
  const refresh = useCallback(() => Promise.all([
    api.get("/admin/stats").then((r) => setStats(r.data)),
    api.get("/admin/settings").then((r) => setBrand(r.data.settings.brand)),
  ]).catch(() => {}), []);
  useEffect(() => {
    refresh();
  }, [refresh]);

  const publish = useCallback(async () => {
    setBusy(true);
    try {
      await api.post("/admin/publish");
      toast.success(t("toast.published"));
      await refresh();
    } catch {
      toast.error(t("toast.publishFailed"));
    } finally {
      setBusy(false);
    }
  }, [refresh, t]);

  return (
    <div className="min-h-screen">
      <aside className="fixed inset-y-0 start-0 z-30 hidden w-64 flex-col gap-8 border-e bg-card/60 p-4 backdrop-blur-xl lg:flex" data-testid="admin-sidebar">
        <Brand t={t} brand={brand} lang={lang} />
        <NavLinks t={t} />
        <div className="mt-auto space-y-1">
          <a href="/" target="_blank" rel="noreferrer" data-testid="sidebar-view-live" className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground"><ExternalLink className="h-4 w-4" />{t("top.viewLive")}</a>
          <button onClick={logout} data-testid="logout-btn" className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground"><LogOut className="h-4 w-4 rtl:rotate-180" />{t("top.logout")}</button>
        </div>
      </aside>
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side={dir === "rtl" ? "right" : "left"} className="w-72 space-y-8" dir={dir}>
          <SheetTitle className="sr-only">{t("nav.menu")}</SheetTitle>
          <Brand t={t} brand={brand} lang={lang} />
          <NavLinks t={t} onNavigate={() => setMenuOpen(false)} />
          <button onClick={logout} data-testid="mobile-logout-btn" className="flex items-center gap-3 px-3 text-sm text-muted-foreground"><LogOut className="h-4 w-4 rtl:rotate-180" />{t("top.logout")}</button>
        </SheetContent>
      </Sheet>
      <div className="lg:ps-64">
        <header className="sticky top-0 z-20 flex h-16 items-center gap-2 border-b bg-background/85 px-4 backdrop-blur-xl sm:px-6">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMenuOpen(true)} aria-label={t("nav.menu")} data-testid="admin-mobile-menu-btn"><Menu className="h-5 w-5" /></Button>
          <PublishControl stats={stats} onPublish={publish} busy={busy} t={t} />
          <div className="ms-auto flex items-center gap-2">
            <Button asChild variant="ghost" size="sm" className="hidden md:inline-flex"><a href="/preview" target="_blank" rel="noreferrer" data-testid="header-preview-link"><Eye className="h-4 w-4" />{t("top.preview")}</a></Button>
            <DashboardColor t={t} />
            <LangSwitch lang={lang} onChange={setLang} testid="admin-lang-switch" />
          </div>
        </header>
        <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
          <Outlet context={{ stats, refresh, publish, publishing: busy }} />
        </main>
      </div>
    </div>
  );
}
